import React from 'react';
import { DefaultButton, MessageBar, MessageBarType, Stack } from '@fluentui/react';
import RichTextField from '../Common/RichTextField';
import TokenReferenceButton from '../Common/TokenReferenceButton';
import { useTokenSuggestions } from '../Common/useTokenSuggestions';
import { useGetEmailTemplateDefaultsQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';
import { buildOrderTemplatePrefill } from './orderEmailTemplate';

/**
 * The order confirmation email's template, with everything that goes around it: the token reference,
 * the source-view typeahead, the default the editor starts from, and a way back to that default.
 *
 * One component because four screens edit this same setting — the per-storefront and global Ordering
 * pages, the global settings form and the storefront details panel — and a template field that
 * behaves differently depending on which screen you opened is worse than no consistency at all.
 *
 * The "Restore the default" button is the escape hatch. Without it, a merchant who clears the field,
 * saves, and comes back has a blank box and nothing telling them what belongs in it — the stock
 * template is on the server, and until now there was no way to ask for it. It only appears once a
 * template is actually saved, because an empty setting already shows the default.
 */
interface IOrderEmailTemplateFieldProps {
    label?: string;
    /** The saved template. Empty means "use the default", which is what the editor then shows. */
    value?: string;
    /** The deprecated Customer Email Text, substituted into the default so it is not lost. */
    customerEmailText?: string;
    onChange: (value: string) => void;
}

const ORDER_TOKENS = (
    <TokenReferenceButton
        context='order'
        description='Copy a token into the template; it is replaced with the corresponding value when the order email is sent. The body also suggests them as you type an opening brace.'
    />
);

const OrderEmailTemplateField: React.FC<IOrderEmailTemplateFieldProps> = (props) => {
    const orderDefault = useGetEmailTemplateDefaultsQuery('order');
    const orderTokens = useTokenSuggestions('order');

    // The default with any deprecated text folded in — what the editor shows for an unset template,
    // and what Restore puts back.
    const defaultBody = buildOrderTemplatePrefill(orderDefault.data?.body, props.customerEmailText);
    const saved = !!props.value?.trim();
    // A blank editor with no default to fall back on is the state worth explaining rather than
    // leaving the merchant to guess at: it means the default could not be fetched, not that the
    // email is empty.
    const defaultUnavailable = !orderDefault.isLoading && !orderDefault.isFetching && !orderDefault.data?.body;

    return (
        <Stack tokens={{ childrenGap: '0.5em' }}>
            <RichTextField
                rawOnly={true}
                mode={'html'}
                label={props.label ?? 'Order Email Template'}
                editorActions={ORDER_TOKENS}
                tokenSuggestions={orderTokens.suggestions}
                value={props.value || defaultBody}
                onChange={(value) => props.onChange(value ?? '')}
            />
            {saved && !!defaultBody && (
                <Stack horizontal>
                    <DefaultButton
                        text='Restore the default'
                        iconProps={{ iconName: 'Refresh' }}
                        title='Replace this template with the standard order confirmation'
                        onClick={() => props.onChange(defaultBody)}
                    />
                </Stack>
            )}
            {defaultUnavailable && !saved && (
                <MessageBar messageBarType={MessageBarType.warning} isMultiline>
                    The standard template could not be loaded, so this box is empty. Leaving it empty is
                    safe — order emails fall back to the standard template — but anything you type here
                    replaces it entirely.
                </MessageBar>
            )}
        </Stack>
    );
};

export default OrderEmailTemplateField;
